/**
 * Review Draft Storage
 * Silently saves review drafts to localStorage for later recovery
 */

interface ReviewDraft {
  itemUin: number;
  rating: number;
  content: string;
  timestamp: number;
  reviewId?: string; // Only for edits
}

const DRAFT_KEY_PREFIX = 'review-draft-';
const DRAFT_EXPIRY_DAYS = 7;

/**
 * Generate storage key for a draft
 */
function getDraftKey(itemUin: number, reviewId?: string): string {
  return reviewId
    ? `${DRAFT_KEY_PREFIX}${itemUin}-edit-${reviewId}`
    : `${DRAFT_KEY_PREFIX}${itemUin}`;
}

/**
 * Save draft to localStorage
 */
export function saveReviewDraft(
  itemUin: number,
  rating: number,
  content: string,
  reviewId?: string,
): void {
  try {
    const draft: ReviewDraft = {
      itemUin,
      rating,
      content,
      timestamp: Date.now(),
      reviewId,
    };

    const key = getDraftKey(itemUin, reviewId);
    localStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    // Silently fail if localStorage is unavailable
    console.warn('Failed to save review draft:', error);
  }
}

/**
 * Load draft from localStorage
 */
export function loadReviewDraft(
  itemUin: number,
  reviewId?: string,
): { rating: number; content: string } | null {
  try {
    const key = getDraftKey(itemUin, reviewId);
    const stored = localStorage.getItem(key);

    if (!stored) return null;

    const draft: ReviewDraft = JSON.parse(stored);

    // Check if draft is expired
    const age = Date.now() - draft.timestamp;
    const maxAge = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    if (age > maxAge) {
      // Draft too old, remove it
      localStorage.removeItem(key);
      return null;
    }

    return {
      rating: draft.rating,
      content: draft.content,
    };
  } catch (error) {
    console.warn('Failed to load review draft:', error);
    return null;
  }
}

/**
 * Clear draft from localStorage
 */
export function clearReviewDraft(itemUin: number, reviewId?: string): void {
  try {
    const key = getDraftKey(itemUin, reviewId);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear review draft:', error);
  }
}

/**
 * Clean up all expired drafts
 * Can be called on app init or periodically
 */
export function cleanupExpiredDrafts(): void {
  try {
    const maxAge = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const now = Date.now();

    // Get all localStorage keys
    const keys = Object.keys(localStorage);

    keys.forEach((key) => {
      if (!key.startsWith(DRAFT_KEY_PREFIX)) return;

      try {
        const stored = localStorage.getItem(key);
        if (!stored) return;

        const draft: ReviewDraft = JSON.parse(stored);
        const age = now - draft.timestamp;

        if (age > maxAge) {
          localStorage.removeItem(key);
        }
      } catch {
        // Invalid draft, remove it
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to cleanup expired drafts:', error);
  }
}
